'use client';

import { ReactElement } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CartItem as CartItemCard } from './cart-item/cart-item.component';
import { CartLaboratoryGroup } from './cart-laboratory-group/cart-laboratory-group.component';
import { CartProfessionalGroup } from './cart-professional-group/cart-professional-group.component';

import { useRemoveFromCart } from '@/hooks/use-remove-from-cart.hook';
import { CartItem } from '@/types/cart.types';
import { CartGroupTypeEnum, CartItemGroup, groupCartItems } from '@/utils/cart/group-cart-items.util';
import { formatCartItemCountdownLabel } from '@/utils/cart/format-cart-item-countdown.util';

function noop() {}

interface CartItemsListProps {
  items: CartItem[];
}

interface RenderGroupHandlers {
  onRemoveItem: (item: CartItem) => void;
  onRemoveItems: (items: CartItem[]) => void;
}

function renderGroup(group: CartItemGroup, handlers: RenderGroupHandlers): ReactElement {
  const { onRemoveItem, onRemoveItems } = handlers;

  switch (group.type) {
    case CartGroupTypeEnum.LABORATORY:
      return (
        <CartLaboratoryGroup
          items={group.items}
          onEditSchedule={noop}
          onEditPatient={noop}
          onRemoveExam={onRemoveItem}
          onRemoveAll={() => onRemoveItems(group.items)}
        />
      );

    case CartGroupTypeEnum.PROFESSIONAL:
      return (
        <CartProfessionalGroup
          items={group.items}
          onEditSchedule={noop}
          onEditPatient={noop}
          onRemove={onRemoveItem}
        />
      );

    case CartGroupTypeEnum.SINGLE:
      return (
        <CartItemCard
          item={group.items[0]}
          warning={formatCartItemCountdownLabel(group.items[0])}
          onEditSchedule={noop}
          onEditPatient={noop}
          onRemove={() => onRemoveItem(group.items[0])}
        />
      );
  }
}

export function CartItemsList({ items }: CartItemsListProps) {
  const { removeItem, removeItems } = useRemoveFromCart();

  const groups = groupCartItems(items);

  const handlers: RenderGroupHandlers = {
    onRemoveItem: (item) => void removeItem(item),
    onRemoveItems: (groupItems) => void removeItems(groupItems),
  };

  return (
    <ul
      className="flex flex-col gap-4 px-6 pb-5"
      data-testid={DataTestIdEnum.CART_ITEMS_LIST}
    >
      {groups.map((group) => (
        <li key={group.id}>{renderGroup(group, handlers)}</li>
      ))}
    </ul>
  );
}
